import React, { useState, useRef } from "react";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileCsv,
  faFileAlt,
  faCheckCircle,
  faSpinner,
  faCloudUploadAlt,
  faDownload,
  faBookOpen,
  faArrowUpFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import "./addStudentsFromCsv.css";

const AddFamilyFromCSV = () => {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "text/csv") {
      validateAndSetFile(droppedFile);
    } else {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload a valid CSV file.",
        confirmButtonColor: "#2E2A74",
      });
    }
  };

  const validateAndSetFile = (selectedFile) => {
    if (
      selectedFile.type !== "text/csv" &&
      !selectedFile.name.endsWith(".csv")
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid File Format",
        text: "Please select a CSV file.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "File size must be less than 5MB.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) {
      Swal.fire({
        icon: "warning",
        title: "No File Selected",
        text: "Please select a CSV file before uploading.",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://mhsdev.online/upload-csv-students", true);

      xhr.setRequestHeader("Accept", "application/json");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentage = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentage);
        }
      };

      xhr.onload = () => {
        setIsUploading(false);

        if (xhr.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Upload Successful!",
            text: "Student data has been successfully imported.",
            confirmButtonColor: "#2E8B57",
            timer: 3000,
          });
          setFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text:
              xhr.responseText || "Failed to upload file. Please try again.",
            confirmButtonColor: "#2E2A74",
          });
        }
        setUploadProgress(0);
      };

      xhr.onerror = () => {
        setIsUploading(false);
        Swal.fire({
          icon: "error",
          title: "Network Error",
          text: "Please check your connection and try again.",
          confirmButtonColor: "#2E2A74",
        });
        setUploadProgress(0);
      };

      xhr.timeout = 300000;
      xhr.ontimeout = () => {
        setIsUploading(false);
        Swal.fire({
          icon: "error",
          title: "Timeout",
          text: "The upload took too long. Please try again.",
          confirmButtonColor: "#2E2A74",
        });
        setUploadProgress(0);
      };

      xhr.send(formData);
    } catch (error) {
      console.error("Upload error:", error);
      setIsUploading(false);
      Swal.fire({
        icon: "error",
        title: "Upload Error",
        text: "An unexpected error occurred.",
        confirmButtonColor: "#2E2A74",
      });
    }
  };

  return (
    <div className="addStudentsCSV-dashboard-wrapper">
      <Sidebar />
      <div className="addStudentsCSV-main-content">
        <section className="addStudentsCSV-content">
          <div className="addStudentsCSV-content-header">
            <h1 className="addStudentsCSV-title">Bulk Student Registration</h1>
            <p className="addStudentsCSV-subtitle">
              Import multiple student records using CSV format
            </p>
          </div>

          {/* Two horizontal cards container */}
          <div className="addStudentsCSV-horizontal-cards-container">
            {/* Instructions Card (Left - 45%) */}
            <div className="addStudentsCSV-horizontal-card addStudentsCSV-instructions-card">
              <div className="addStudentsCSV-card-header">
                <div className="addStudentsCSV-header-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faBookOpen}
                    className="addStudentsCSV-card-icon"
                  />
                </div>
                <div>
                  <h3 className="addStudentsCSV-card-title">
                    Import Guidelines
                  </h3>
                  <p className="addStudentsCSV-card-subtitle">
                    Ensure successful data import by following these steps
                  </p>
                </div>
              </div>

              <div className="addStudentsCSV-instructions-content">
                <div className="addStudentsCSV-instruction-step">
                  <div className="addStudentsCSV-step-number">1</div>
                  <div className="addStudentsCSV-step-content">
                    <h4 className="addStudentsCSV-step-title">
                      Review Template Structure
                    </h4>
                    <p className="addStudentsCSV-step-description">
                      Download and review the sample CSV file to understand the
                      required format
                    </p>
                  </div>
                </div>

                <div className="addStudentsCSV-instruction-step">
                  <div className="addStudentsCSV-step-number">2</div>
                  <div className="addStudentsCSV-step-content">
                    <h4 className="addStudentsCSV-step-title">
                      Prepare Your Data
                    </h4>
                    <p className="addStudentsCSV-step-description">
                      Ensure all required fields are filled and follow the
                      specified format guidelines
                    </p>
                  </div>
                </div>

                <div className="addStudentsCSV-instruction-step">
                  <div className="addStudentsCSV-step-number">3</div>
                  <div className="addStudentsCSV-step-content">
                    <h4 className="addStudentsCSV-step-title">
                      Validate Before Upload
                    </h4>
                    <p className="addStudentsCSV-step-description">
                      Check for data consistency and avoid common formatting
                      errors
                    </p>
                  </div>
                </div>
              </div>

              <div className="addStudentsCSV-card-actions">
                <a
                  href="/family-csv-sample.csv"
                  className="addStudentsCSV-action-btn addStudentsCSV-primary-action"
                  download
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Download Sample CSV</span>
                </a>
                <a
                  href="/familyCSV-Instructions.docx"
                  className="addStudentsCSV-action-btn addStudentsCSV-secondary-action"
                  download
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>Download Instructions</span>
                </a>
              </div>
            </div>

            {/* Upload Card (Right - 45%) */}
            <div className="addStudentsCSV-horizontal-card addStudentsCSV-upload-card">
              <div className="addStudentsCSV-card-header">
                <div className="addStudentsCSV-header-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faArrowUpFromBracket}
                    className="addStudentsCSV-card-icon"
                  />
                </div>
                <div>
                  <h3 className="addStudentsCSV-card-title">Upload CSV File</h3>
                  <p className="addStudentsCSV-card-subtitle">
                    Select or drag & drop your prepared CSV file
                  </p>
                </div>
              </div>

              <div
                className={`addStudentsCSV-upload-area ${
                  isDragOver ? "addStudentsCSV-drag-over" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="addStudentsCSV-upload-icon-wrapper">
                  <FontAwesomeIcon
                    icon={faCloudUploadAlt}
                    className="addStudentsCSV-upload-area-icon"
                  />
                </div>
                <h4 className="addStudentsCSV-upload-area-title">
                  Drag & Drop Your File
                </h4>
                <p className="addStudentsCSV-upload-subtitle">
                  Supported format: .csv (Max 5MB)
                </p>

                <div className="addStudentsCSV-upload-actions">
                  <label
                    htmlFor="file-upload"
                    className="addStudentsCSV-browse-btn"
                  >
                    Browse Files
                    <input
                      ref={fileInputRef}
                      id="file-upload"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="addStudentsCSV-upload-file-input"
                    />
                  </label>
                  <p className="addStudentsCSV-upload-note">
                    or drop your file anywhere in this area
                  </p>
                </div>
              </div>

              {file && (
                <div className="addStudentsCSV-file-preview-section">
                  <div className="addStudentsCSV-file-preview-header">
                    <FontAwesomeIcon
                      icon={faFileCsv}
                      className="addStudentsCSV-file-type-icon"
                    />
                    <span>Selected File</span>
                  </div>
                  <div className="addStudentsCSV-file-details">
                    <div className="addStudentsCSV-file-info">
                      <span className="addStudentsCSV-file-name">
                        {file.name}
                      </span>
                      <span className="addStudentsCSV-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div className="addStudentsCSV-file-status">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="addStudentsCSV-status-icon"
                      />
                      <span>Ready to upload</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="addStudentsCSV-upload-controls">
                <button
                  className={`addStudentsCSV-upload-btn ${
                    !file || isUploading ? "addStudentsCSV-disabled" : ""
                  }`}
                  onClick={handleSubmit}
                  disabled={isUploading || !file}
                >
                  {isUploading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Uploading... {uploadProgress}%</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} />
                      <span>Upload File</span>
                    </>
                  )}
                </button>
              </div>

              {isUploading && (
                <div className="addStudentsCSV-upload-progress-section">
                  <div className="addStudentsCSV-progress-header">
                    <span>Upload Progress</span>
                    <span className="addStudentsCSV-progress-percentage">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="addStudentsCSV-progress-bar-container">
                    <div
                      className="addStudentsCSV-progress-bar-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="addStudentsCSV-progress-note">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Please wait while we process your file...
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AddFamilyFromCSV;
